// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { useContext, useMemo } from 'react';
import styled from '@emotion/styled';

import { useDashboardCalendarPage } from './hooks';
import { mediaMax, spacing } from 'presentations/views/styles';
import DashboardPageTemplate from 'presentations/views/components/templates/DashboardPageTemplate';
import DashboardEventCreateHeader from 'presentations/views/components/organisms/DashboardEventCreateHeader';
import DashboardEventCreateCheckList from 'presentations/views/components/organisms/DashboardEventCreateCheckList';
import { ViewerItem } from 'presentations/api/schema/user';
import { gray04, white } from 'presentations/views/styles/color';
import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import DashboardEventCreateNavigation from 'presentations/views/components/organisms/DashboardEventCreateNavigation';
import EventPopover from 'presentations/views/components/organisms/EventPopover';
import EventCreatePopover from 'presentations/views/components/molecules/EventCreatePopover';
import DashboardCalendar from 'presentations/views/components/organisms/DashboardCalendar';
import TimeZoneListPopover from 'presentations/views/components/organisms/TimeZoneListPopover';
import TimeZoneListItemPopover from 'presentations/views/components/organisms/TimeZoneListItemPopover';
// import DashboardEventStackView from 'presentations/views/components/organisms/DashboardEventStackView';
// import DashboardEventBlockView from 'presentations/views/components/organisms/DashboardEventBlockView';
import { css } from '@emotion/react';
import { navigationWidth } from '../../organisms/DashboardNavigation';
import DashboardCalendarSettingMenu from 'presentations/views/components/molecules/DashboardCalendarSettingMenu';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';
import Button from 'presentations/views/components/atoms/Button';
import dynamic from 'next/dynamic';
import { CalendarWeekDay } from '../../atoms/Calendar/reducer';
const HeaderCalendarErrorMessage = dynamic(() =>
  import('presentations/views/components/organisms/HeaderCalendarErrorMessage'),
);

const subWidth = 320;

interface Props {
  viewer?: ViewerItem;
  startWeekday: CalendarWeekDay;
  // viewType: EventCreateViewType;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state: dashboardState } = useContext(DashboardPortalContext);
  const viewer = props.viewer || dashboardState.viewer!;
  const { state, actions } = useDashboardCalendarPage({
    viewer,
    startWeekday: props.startWeekday,
    // viewType: props.viewType,
  });
  const selectedUserIds = state.selectedTeamUsers.map((u) => u.userId);

  const header = (
    <>
      {dashboardState.hasCalendarIntegrationError && (
        <HeaderCalendarErrorMessage />
      )}
      <DashboardEventCreateHeader
        viewer={viewer}
        isOpenUserList={state.isOpenUserList}
        onClickOpenUserList={actions.onClickOpenUserList}
        // viewType={state.viewType}
        onChangeTerm={actions.onChangeTerm}
        selectableTeamUsers={state.selectableTeamUsers}
        selectedTeamUsers={state.selectedTeamUsers}
        onClickSetting={actions.onClickSetting}
      />
      {state.isOpenUserList && (
        <DashboardEventCreateNavigation
          viewer={viewer}
          onClickFilter={actions.onClickFilter}
          onClickUser={actions.onSelectUser}
          isOpenFilter={state.isOpenFilter}
          selectableTeamUsers={state.selectableTeamUsers}
          selectedTeamUsers={state.selectedTeamUsers}
        />
      )}
    </>
  );

  return (
    <>
      <DashboardPageTemplate
        title={t('title for event')}
        viewer={viewer}
        tabBarContent={
          <Content>
            <Main isShowSub={state.isShowSub}>
              <DashboardCalendar
                header={header}
                viewer={viewer}
                events={state.events}
                isLoading={state.isLoading}
                onClickEvent={actions.onClickCalendarEvent}
                onClickMoreEvent={actions.onClickCalendarMoreEvent}
                onClickDay={actions.onClickCalendarDay}
                onUpdateSchedule={actions.onUpdateSchedule}
                onClickAddTimeZone={actions.onClickAddTimeZone}
                onClickTimeZone={actions.onClickTimeZone}
                selectedEventId={state.selectedEventId || undefined}
              />
            </Main>
            <Sub isShowSub={state.isShowSub}>
              {state.creatingEvent && state.creatingElement ? (
                <EventCreatePopover
                  isPopover={!state.isShowSub}
                  viewer={viewer}
                  userApp={state.userApp}
                  timeZone={state.timeZone}
                  element={state.creatingElement}
                  isDraggingEvent={false}
                  onClose={actions.onCloseEventCreate}
                  onCreateEvent={actions.onCreateEventSuccess}
                  selectedUserIds={[viewer.uid]}
                />
              ) : (
                state.selectedEvent && (
                  <EventPopover
                    isDrawer={state.isMobile}
                    isPopover={!state.isShowSub}
                    viewer={viewer}
                    timeZone={state.timeZone}
                    mergedEvent={state.selectedEvent.mergedEvent}
                    element={state.selectedEvent.element}
                    userId={state.selectedEvent.userId}
                    onClose={actions.onClickCloseEvent}
                    onEditEvent={actions.onEditEvent}
                    onCancelledEvent={actions.onCancelledEvent}
                    onChangeResponseStatus={actions.onChangeResponseStatus}
                    userApp={state.userApp}
                  />
                )
              )}
            </Sub>
          </Content>
        }
      />
      <DashboardEventCreateCheckList
        isOpen={state.isMobileFilterOpen}
        viewer={viewer}
        isLoadingTeamUsers={state.isLoadingTeamUsers}
        viewerTeamUser={state.viewerTeamUser}
        selectableTeamUsers={state.selectableTeamUsers}
        selectableUserUids={state.selectableUserUids}
        onChangeUserSort={actions.onChangeUserSort}
        onChangeUserAdd={actions.onChangeUserAdd}
        onChangeUserDelete={actions.onChangeUserDelete}
        onClickInvite={actions.onClickInvite}
        onCloseFilter={actions.onCloseFilter}
      />
      <DashboardCalendarSettingMenu
        anchor={state.settingAnchor}
        viewer={viewer}
        onClose={actions.onCloseSetting}
        // viewType={state.viewType}
        // duration={state.duration}
        showRange={state.showRange}
        // interval={state.interval}
        // onChangeDuration={actions.onChangeDuration}
        // onChangeInterval={actions.onChangeInterval}
        onChangeView={actions.onChangeView}
        // onChangeViewType={actions.onChangeViewType}
        onChangeStartWeekday={actions.onChangeStartWeekday}
        startWeekday={state.startWeekdaySetting}
        onChangeIsShowSameEventMerge={actions.onChangeIsShowSameEventMerge}
        isShowSameEventMerge={state.isShowSameEventMerge}
        onChangeIsShowEventOverlap={actions.onChangeIsShowEventOverlap}
        isShowEventOverlap={state.isShowEventOverlap}
        isShowWeekends={state.isShowWeekends}
        isShowDeclinedEvent={state.isShowDeclinedEvent}
        onChangeIsShowDeclinedEvent={actions.onChangeIsShowDeclinedEvent}
        timeFormat={state.timeFormat}
      />

      {state.timeZoneAnchor && (
        <TimeZoneListPopover
          title={t('Add TimeZone')}
          anchor={state.timeZoneAnchor}
          onClose={actions.onCloseAddTimeZone}
          onSelect={actions.onSelectNewTimeZone}
        />
      )}
      {state.timeZoneMenuAnchor && state.selectedTimeZone && (
        <TimeZoneListItemPopover
          timeZone={state.selectedTimeZone}
          anchor={state.timeZoneMenuAnchor}
          onClose={actions.onCloseTimeZoneMenu}
          onDelete={actions.onDeleteTimeZoneMenu}
        />
      )}
      {state.confirmResponseStatus !== null && (
        <ConfirmationDialog
          title={t('confirmResponseStatusTitle')}
          text={t('confirmResponseStatusText')}
          isOpen={true}
          isLoading={state.isConfirmLoading}
          submitField={
            <RecurringSubmitContainer>
              <StyledButton
                isDisabled={state.isConfirmLoading}
                onClick={actions.onSubmitConfirm}
                size="large"
                color="primary"
              >
                {t('This Event')}
              </StyledButton>
              <StyledButton
                isDisabled={state.isConfirmLoading}
                onClick={actions.onSubmitRecurringConfirm}
                size="large"
                color="primary"
              >
                {t('All Events')}
              </StyledButton>
              <StyledButton
                isDisabled={state.isConfirmLoading}
                onClick={actions.onCloseConfirm}
                size="large"
              >
                {t('Cancel')}
              </StyledButton>
            </RecurringSubmitContainer>
          }
        />
      )}
    </>
  );
}
const Content = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Main = styled.div<{ isShowSub: boolean }>`
  flex: 1;
  width: ${(props) =>
    props.isShowSub
      ? `calc(100vw - ${subWidth + navigationWidth}px)`
      : `calc(100vw - ${navigationWidth}px)`};
  display: flex;
  flex-direction: column;
  background-color: ${white};
  height: inherit;

  ${mediaMax.tablet} {
    max-width: 100%;
  }
`;

const Sub = styled.div<{ isShowSub: boolean }>`
  ${(props) =>
    props.isShowSub &&
    css`
      position: sticky;
      top: 0;
      height: 100vh;
      border-left: 1px solid ${gray04(props)};
      width: 320px;
      background-color: ${white(props)};
      overflow-y: auto;
    `};
`;

const RecurringSubmitContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledButton = styled(Button)`
  margin-bottom: ${spacing(16)};
  width: 100%;
`;
